
  import Vue from 'vue'
  import ReadingService from '@/services/ReadingService';
  import DeviceService from '@/services/DeviceService';
  import ImageService from '@/services/ImageService';
  import { autoComplete, extractErrorMessage, parseCSV } from '@/utils/Util';

  import DateTimePicker from '@/components/misc/DateTimePicker.vue'
  import { ReadingFlagTypes } from './ReadingCollections';

  export default Vue.extend({
    name: 'ReadingImportDialog',

    components: {
      DateTimePicker
    },
    props: ['device', 'timestamp'],
    data: (vm: any): any => ({
        readingFlagTypes: ReadingFlagTypes,
        dialog: false,
        valid: true,
        alertText: "",
        alertVisible: false,
        alertType: "error",
        loading: false,
        loadingDevice: false,
        requiredRule: [
            (v: string) => !!v || vm.requiredError
        ],
        numberRule: [
            (v: string) => !!v || vm.requiredError,
            (v: string) => !isNaN(parseFloat(v)) || vm.numberError,
        ],
        requiredSelect: [
            (v: any) => (!!v && Object.keys(v).length > 0) || vm.requiredError
        ],
        fileRules: [
          (v: any) => !v || v.size < 48000000 || vm.imageSizeError,
        ],
        devices: [] as any[],
        selectedDevice: {} as any,
        readings: [],
        dataFile: null,
        lastReading: {} as any,
        showLastReadingImageProof: false,
        lastReadingImageProofLoading: true,
        lastReadingImageProofData: "" as any,
        parsedFileData: [] as any[]
    }),
    created() {
      if(this.device) {
        this.selectedDevice = this.device;
      }
    },
    methods: {
      validate() {
        if(this.createForm.validate()) {
          this.createReadings();
        }
      },
      close() {
        this.dialog = false;
      },
      clear() {
        this.readings = [];
        this.devices = [];
        this.selectedDevice = {};
        this.lastReading = {};
        this.lastReadingImageProofData = "";
        this.lastReadingImageProofLoading = false;
        this.showLastReadingImageProof = false;
        this.dataFile = null;
        this.alertVisible = false;
        this.loading = false;
        this.parsedFileData = [];
      },
      dismiss() {
        this.clear();
        this.close();
      },
      async createReadings() {
        this.loading = true;
        const deviceUUID = this.selectedDevice.uuid;

        // Batch create data in chunks of 1000
        const batchSize = 1000;

        for (let i = 0; i < this.parsedFileData.length; i += batchSize) {
          const batch = this.parsedFileData.slice(i, i + batchSize).map((value: any) => ({ ...value, deviceId: deviceUUID }));

          try {
            const result = await ReadingService.batchCreate(this.currentTenant, batch, false);
            
            if(result.some((value: any) => value?.type === "errors")) {
              throw "Errors in creating readings."
            }
          } catch(err: any) {
            console.log(err, err.response);  
            this.alertType = "error";
            this.alertText = this.$t('reading.errorCreate') + ' ' + extractErrorMessage(err);
            this.alertVisible = true;
            this.loading = false;
          }
        }
      },
      getDevices() {
        this.loadingDevice = true;

        DeviceService.getAllCollectionPages(this.currentTenant).then((devices: any[]) => {
          this.loadingDevice = false;

          this.devices = devices;
        }).catch((err: any) => {
          console.log(err, err.response);  
          this.alertType = "error";
          this.alertText = this.$t('device.loadingError') + ' ' + extractErrorMessage(err);
          this.alertVisible = true;    
          this.loadingDevice = false;  
        });
      },
      getLastReading() {
        this.loading = true;

        ReadingService.getLastReading(this.currentTenant, this.selectedDevice).then((lastReading: any) => {
          this.loading = false;

          if(!lastReading)
            lastReading = {}

          this.lastReading = lastReading;
        }).catch((err: any) => {
          console.log(err, err.response);
          this.loading = false;    
        });
      },
      getImageProof() {
          this.showLastReadingImageProof = true;
          this.lastReadingImageProofLoading = true;

          if(this.lastReading && Object.keys(this.lastReading).length > 0) {
            ImageService.getImage('readings', this.lastReading.uuid).then((imageData) => {
              this.loading = false;
              this.lastReadingImageProofLoading = false;
              this.lastReadingImageProofData = imageData;
            }).catch((err) => {
                console.log(err);
                console.log(err.response);
                this.lastReadingImageProofLoading = false;
                this.lastReadingImageProofData = "";
            });
          }
      },
      autoComplete,
    },
    watch: {
      async dataFile() {
        if(this.dataFile) {
          this.loading = true;
          const datastring = await this.dataFile.text();
          const parsed = parseCSV(datastring, ';');

          if(!("timestamp" in parsed[0]) || !("value" in parsed[0])) {
            this.alertType = "error";
            this.alertText = "Invalid columns. Needed: timestamp, value, flag, factorApplied"
            this.alertVisible = true;
            this.loading = false;
            return;
          }

          this.parsedFileData = parsed.map((value: Record<string, string>) => ({
            timestamp: value['timestamp'],
            value: value['factorApplied'] === "true" && !isNaN(parseFloat(value['factor'])) ? parseFloat(value['value']) / parseFloat(value['factor']) : value['value'],
            flag: value['flag'].length > 0 ? value['flag'] : undefined,
            factor: !isNaN(parseFloat(value['factor'])) ? parseFloat(value['factor']) : undefined,
          }));

          this.loading = false;
        } else {
          this.parsedFileData = [];
        }
      },
      dialog(value: boolean) {
        if(value) {
          if(this.device) {
            this.selectedDevice = this.device;
          }
          this.getDevices();
        } else {
          this.clear();
        }
      },
      showLastReadingImageProof(value: boolean) {
        if(value && this.lastReadingImageProofData.length === 0) {
          this.getImageProof();
        }
      },
      selectedDevice(value: any) {
        if(value && Object.keys(value).length > 0) {
          this.getLastReading();
        }
      }
    },
    computed: {
      validationRulesValid(): any {
        let valid = true;
        return valid;
      },
      formattedLastReadingTimestamp(): any {
        if(this.lastReading && 'timestamp' in this.lastReading) {
          return new Date(this.lastReading.timestamp).toLocaleString();
        } else {
          return "";
        }
      },
      createForm(): any {
          return this.$refs.createForm;
      },
      currentTenant(): any {
        return this.$root.$store.state.session.selectedTenant.uuid;
      },
      requiredError(): any {
          return this.$t('required');
      },
      numberError(): any {
          return this.$t('mustNumber');
      },
      imageSizeError(): any {
        return "Max file size 48MB"
      }
    }
  })
